import { createAsyncThunk, createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { getThunkRejectMessage } from 'app/utils/apiError';
import axios from 'axios';

export const getSuppliers = createAsyncThunk(
  'eCommerceApp/rfqs/viewSupplier',
  async ({ suppliers, params }, { dispatch }) => {
    const response = await axios.get(`/api/suppliers`, { params });
    const data = await response.data.data;
    // dispatch(setPreviousSuppliers(suppliers))
    return data;
  }
);

export const getSentToSuppliers = createAsyncThunk(
  'eCommerceApp/rfqs/getSentToSuppliers',
  async (id, { dispatch }) => {
    const response = await axios.get(`/api/getSupplier?order_id=${id}`);
    const data = await response.data.suppliers;
    dispatch(setPreviousSuppliers(data))
    return data;
  }
);

export const getSupplierOrder = createAsyncThunk(
  'eCommerceApp/rfqs/getSupplierOrder',
  async ({ id, params }, { dispatch }) => {
    const response = await axios.get(`/api/getSupplierOrder/${id}`, { params });
    const data = await response.data.suppliers;
    return data;
  }
);

export const receiveSupplierShipmentOrder = createAsyncThunk(
  'eCommerceApp/rfqs/receiveSupplierShipmentOrder',
  async ({ payload }, { dispatch }) => {
    const response = await axios.post(`/api/receiveSupplierShipment`, payload);
    const data = await response.data.data;
    return data;
  }
);

/**
 * PGSS-5331: what this round has to hand on to a supplier — the note staff wrote on Make it In Review and
 * the revision documents attached with it. Fetched when the Send to Supplier dialog opens so the screen can
 * show and pre-fill it; staff can then amend the note, clear it, or deselect any document, and only what
 * survives that screen is sent.
 *
 * Never rejects the caller's flow: an unreachable hand-off means "nothing to pre-fill", which is a
 * legitimate state (a round that carried no note and no documents looks the same). Failing loudly here
 * would block a send that is otherwise perfectly valid.
 */
export const getSupplierRoundHandoff = createAsyncThunk(
  'eCommerceApp/rfqs/getSupplierRoundHandoff',
  async ({ id }) => {
    try {
      const response = await axios.get(`/api/supplierRoundHandoff/${id}`);
      return response.data || {};
    } catch (error) {
      return {};
    }
  }
);

export const quoteToSupplier = createAsyncThunk(
  'eCommerceApp/rfqs/requestForQuoteToSupplier',
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`/api/requestForQuoteToSupplier/${id}`, payload);
      const data = await response.data.data;
      return data;
    } catch (error) {
      return rejectWithValue(getThunkRejectMessage(error));
    }
  }
);

export const quoteToOrder = createAsyncThunk(
  'eCommerceApp/rfqs/convertQuoteToOrder',
  async ({ id, payload, params }, { dispatch }) => {
    const response = await axios.put(`/api/convertQuoteToOrder/${id}`, payload, { params });
    const data = await response.data;
    // updated response to show the error message after the api call
    return !data?.error ? data.data : data;
  }
);

const rfqsAdapter = createEntityAdapter({});

export const { selectAll: selectRfqs, selectById: selectRfqsById } =
  rfqsAdapter.getSelectors((state) => state.eCommerceApp.rfqs);

const rfqsSlice = createSlice({
  name: 'eCommerceApp/rfqs',
  initialState: rfqsAdapter.getInitialState({
    customer: null,
    rfqsStatuses: [],
    suppliersList: [],
    previousSuppliers: [],
    suppliersListLoading: false
  }),
  reducers: {
    setPreviousSuppliers: (state, action) => {
      state.previousSuppliers = action.payload
    }
  },
  extraReducers: {
    [getSuppliers.fulfilled]: (state, action) => {
      state.suppliersList = action.payload
      state.suppliersListLoading = false
    },
    [getSuppliers.rejected]: (state, action) => {
      state.suppliersListLoading = false
    },
    [getSuppliers.pending]: (state, action) => {
      state.suppliersList = []
      state.previousSuppliers = []
      state.suppliersListLoading = true
    },
    [getSupplierOrder.fulfilled]: (state, action) => {
      state.suppliersList = action.payload
      state.suppliersListLoading = false
    },
    [getSupplierOrder.rejected]: (state, action) => {
      state.suppliersListLoading = false
    },
    [getSupplierOrder.pending]: (state, action) => {
      state.suppliersList = []
      state.previousSuppliers = []
      state.suppliersListLoading = true
    }
  },
});

export const { setPreviousSuppliers } = rfqsSlice.actions;

export default rfqsSlice.reducer;
